import React from 'react';

import {
  Card,
  Divider,
  Flex,
  Icon, // eslint-disable-next-line no-restricted-imports -- fixme(@retail) use appropriate link from platform/react-router
  Link,
  Text,
} from '@opendoor/bricks/core';

import { formatCurrency, formatPhoneNumber } from 'shared/helpers/formatters';
import { useMobileDevice } from 'shared/helpers/hooks/useMobileDevice';

import { CostBreakdown } from 'buying/components/buyDirectCheckout/CostBreakdown';

import { FileUpload } from 'sellDirect/helpers/FileUploadClient';

interface IContractDetailsProps {
  buyDirectDiscount: number;
  buyerDetails: {
    name: string;
    email: string;
    phone: string;
  };
  coBuyerDetails?: {
    name: string;
    email: string;
    phone: string;
  };
  closeOfEscrow: string;
  downPaymentAmount?: number;
  downPaymentPercentage?: number;
  fundingSource: string;
  listPrice: number;
  loanAmount?: number;
  preApprovalDoc?: FileUpload;
}

/**
 * ContractDetails step
 * storybook-check-ignore
 */
export const ContractDetailsModule: React.FC<IContractDetailsProps> = React.memo(
  ({
    buyDirectDiscount,
    buyerDetails,
    coBuyerDetails,
    closeOfEscrow,
    downPaymentAmount,
    downPaymentPercentage,
    fundingSource,
    listPrice,
    loanAmount,
    preApprovalDoc,
  }) => {
    const { isMobile } = useMobileDevice();
    return (
      <Flex
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="flex-start"
        width="100%"
        mb={8}
      >
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          width="100%"
          justifyContent="space-between"
          mt={[-4, null, 8]}
        >
          <Text size={isMobile ? '160' : '150'} fontWeight="medium" mt={4}>
            Buyer info
          </Text>
          <Flex flexDirection={['column', null, 'row']} justifyContent="space-between" width="100%">
            <Flex flexDirection="column" alignItems="flex-start" width="100%" mt={8}>
              <Text size="120" fontWeight="medium">
                Full name
              </Text>
              <Text size="120" fontWeight="regular" mt={4}>
                {buyerDetails?.name}
              </Text>
            </Flex>
            <Flex flexDirection="column" width="100%" mt={8}>
              <Text size="120" fontWeight="medium">
                Email
              </Text>
              <Text size="120" fontWeight="regular" mt={4}>
                {buyerDetails?.email}
              </Text>
            </Flex>
          </Flex>
          <Flex flexDirection="column" width="100%" mt={8}>
            <Text size="120" fontWeight="medium">
              Phone
            </Text>
            <Text size="120" fontWeight="regular" mt={4}>
              {formatPhoneNumber(buyerDetails?.phone)}
            </Text>
          </Flex>
        </Flex>
        {coBuyerDetails && (
          <Flex
            flexDirection="column"
            alignItems="flex-start"
            width="100%"
            justifyContent="space-between"
            mt={[7, null, 8]}
          >
            <Text size={isMobile ? '160' : '150'} fontWeight="medium" mt={4}>
              Co-buyer info
            </Text>
            <Flex
              flexDirection={['column', null, 'row']}
              justifyContent="space-between"
              width="100%"
            >
              <Flex flexDirection="column" alignItems="flex-start" width="100%" mt={8}>
                <Text size="120" fontWeight="medium">
                  Full name
                </Text>
                <Text size="120" fontWeight="regular" mt={4}>
                  {coBuyerDetails?.name}
                </Text>
              </Flex>
              <Flex flexDirection="column" width="100%" mt={8}>
                <Text size="120" fontWeight="medium">
                  Email
                </Text>
                <Text size="120" fontWeight="regular" mt={4}>
                  {coBuyerDetails?.email}
                </Text>
              </Flex>
            </Flex>
            <Flex flexDirection="column" width="100%" mt={8}>
              <Text size="120" fontWeight="medium">
                Phone
              </Text>
              <Text size="120" fontWeight="regular" mt={4}>
                {formatPhoneNumber(coBuyerDetails?.phone)}
              </Text>
            </Flex>
          </Flex>
        )}
        <Divider mt={8} width="100%" />
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          width="100%"
          justifyContent="space-between"
          mt={8}
        >
          <Text size={isMobile ? '160' : '150'} fontWeight="medium">
            Finance info
          </Text>
          <Flex flexDirection={['column', null, 'row']} justifyContent="space-between" width="100%">
            <Flex flexDirection="column" alignItems="flex-start" width="100%" mt={8}>
              <Text size="120" fontWeight="medium">
                Loan type
              </Text>
              <Text size="120" fontWeight="regular" mt={4}>
                {fundingSource}
              </Text>
            </Flex>
            {downPaymentAmount && downPaymentPercentage && fundingSource !== 'Cash' && (
              <Flex flexDirection="column" width="100%" mt={8}>
                <Text size="120" fontWeight="medium">
                  Down payment
                </Text>
                <Text size="120" fontWeight="regular" mt={4}>
                  {formatCurrency(downPaymentAmount)} {`(${Math.round(downPaymentPercentage)}%)`}
                </Text>
              </Flex>
            )}
          </Flex>
          <Flex flexDirection={['column', null, 'row']} justifyContent="space-between" width="100%">
            {loanAmount && fundingSource !== 'Cash' && (
              <Flex flexDirection="column" width="100%" mt={8}>
                <Text size="120" fontWeight="medium">
                  Loan amount
                </Text>
                <Text size="120" fontWeight="regular" mt={4}>
                  {formatCurrency(loanAmount)}
                </Text>
              </Flex>
            )}
            <Flex flexDirection="column" alignItems="flex-start" width="100%" mt={8}>
              <Text size="120" fontWeight="medium">
                Close of escrow
              </Text>
              <Text size="120" fontWeight="regular" mt={4}>
                {closeOfEscrow}
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <Divider mt={8} width="100%" />
        <CostBreakdown
          buyDirectDiscount={buyDirectDiscount}
          headline="Price details"
          listPrice={listPrice}
          downPaymentAmount={downPaymentAmount}
          downPaymentPercentage={downPaymentPercentage}
        />
        <Divider mt={8} width="100%" />
        {preApprovalDoc && (
          <Flex
            flexDirection="column"
            alignItems="flex-start"
            width="100%"
            justifyContent="space-between"
            mt={8}
          >
            <Text size={isMobile ? '160' : '150'} fontWeight="medium">
              Pre-approval letter
            </Text>
            <Card variant="outline" mt={8} width={['90%', null, '100%']}>
              <Flex flexDirection="row" alignItems="center">
                <Icon size={24} name="offer-inactive" color="neutrals100" aria-hidden="true" />
                <Link
                  href={preApprovalDoc?.getUrl}
                  size="sm"
                  target="_blank"
                  variant="inline-secondary"
                  aria-label={preApprovalDoc?.file?.name}
                  analyticsName="pre-approval-letter-link"
                >
                  <Text size="110" fontWeight="medium" ml={4} mr={6}>
                    {preApprovalDoc?.file?.name}
                  </Text>
                </Link>
              </Flex>
            </Card>
          </Flex>
        )}
      </Flex>
    );
  },
);

ContractDetailsModule.displayName = 'ContractDetailsModule';
